import { ColumnSizing } from '../tables-context';
import { MRT_ColumnDef } from 'material-react-table';
import { getAmount, getBinLast4, getTableDate, IDisputeItem, IPotentialMatch } from './disputes-data';
import { TableCell } from '../../../components/table/TableCell';
import React, { FC } from 'react';
import { CopyComponent } from '../../../components/CopyComponent';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import {
	PotentialMatchColumn
} from '../../../components/disputes/disputes_queues/disputes_queue_details/PotentialMatchColumn';

import { DateComponent } from '../../../components/DateComponent';
import { useLocation } from 'react-router';
import { Link, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { useDisputesContext } from '../../disputes/disputes-context';
import { RELATED_ENTITY_TYPE, RelatedEntityNameLink } from '../../../components/merchants/RelatedEntityNameLink';

export enum DisputesQueueItemsTableAccessorKey {
	createdOn = 'createdOn',
	requestDate = 'requestDate',
	authorizationId = 'authorizationId',
	disputeType = 'disputeType',
	stage = 'stage',
	providerName = 'providerName',
	amount = 'amount',
	cardBrand = 'cardBrand',
	bin = 'bin',
	arn = 'arn',
	transactionId = 'transactionId',
	orderId = 'orderId',
	mid = 'mid',
	transactionDate = 'transactionDate',
	isRefunded = 'isRefunded',
	descriptor = 'descriptor',
	currency = 'currency',
	isWebhook = 'isWebhook',
	transactionMatches = 'id',
	dynamicDescriptor = 'dynamicDescriptor',
	disputeQueueName = 'disputeQueueName',
	potentialMatches = 'potentialMatches',
}

export const defaultDisputesQueueItemsColumnSizing: ColumnSizing = {
	[DisputesQueueItemsTableAccessorKey.transactionId]: 50,
	[DisputesQueueItemsTableAccessorKey.createdOn]: 100,
	[DisputesQueueItemsTableAccessorKey.requestDate]: 100,
	[DisputesQueueItemsTableAccessorKey.authorizationId]: 100,
	[DisputesQueueItemsTableAccessorKey.disputeType]: 90,
	[DisputesQueueItemsTableAccessorKey.stage]: 100,
	[DisputesQueueItemsTableAccessorKey.providerName]: 100,
	[DisputesQueueItemsTableAccessorKey.amount]: 70,
	[DisputesQueueItemsTableAccessorKey.cardBrand]: 70,
	[DisputesQueueItemsTableAccessorKey.bin]: 70,
	[DisputesQueueItemsTableAccessorKey.arn]: 70,
	[DisputesQueueItemsTableAccessorKey.isWebhook]: 90,
	[DisputesQueueItemsTableAccessorKey.disputeQueueName]: 90,
	// [DisputesQueueItemsTableAccessorKey.transactionDate]: 100,
	// [DisputesQueueItemsTableAccessorKey.isRefunded]: 100,
	// [DisputesQueueItemsTableAccessorKey.descriptor]: 120,
	// [DisputesQueueItemsTableAccessorKey.currency]: 50,
};

export enum DisputesQueueTableAccessorKey {
	view = 'view',
	name = 'name',
	partnerId = 'partnerId',
	createdOn = 'createdOn',
	errorCount = 'errorCount',
	totalCount = 'totalCount',
	loadedRecordsCount = 'loadedRecordsCount',
	processedRecordsCount = 'processedRecordsCount',
	recordsCount = 'recordsCount',
	errors = 'errors',

}

export const defaultDisputesQueueColumnSizing: ColumnSizing = {
	[DisputesQueueTableAccessorKey.view]: 50,
	[DisputesQueueTableAccessorKey.createdOn]: 50,
	[DisputesQueueTableAccessorKey.name]: 70,
	[DisputesQueueTableAccessorKey.partnerId]: 70,
	[DisputesQueueTableAccessorKey.errorCount]: 20,
	[DisputesQueueTableAccessorKey.totalCount]: 20,
	[DisputesQueueTableAccessorKey.loadedRecordsCount]: 20,
	[DisputesQueueTableAccessorKey.processedRecordsCount]: 20,
	[DisputesQueueTableAccessorKey.recordsCount]: 20,
	[DisputesQueueTableAccessorKey.errors]: 70,
};

export interface IDisputeQueueItem {
	kountEventType?: string;
	eventType?: string;
	amount: number;
	arn: string;
	bin: string;
	cardBrand: string;
	createdOn: Date;
	currency: string;
	descriptor : string;
	disputeActivities: IDisputeItem[];
	disputeManagementSystem: string;
	disputeType: string;
	hasMatch: boolean;
	id: string;
	isRefunded: boolean;
	isReviewRequired: boolean;
	isWebhook?: boolean;
	last4: string;
	message: string;
	meta: string;
	mid: string;
	orderId: string;
	providerName: string;
	requestDate: string;
	authorizationDate: string;
	authorizationId: string;
	stage: string;
	transactionDate: string;
	transactionId: string;
	transactionMatches: ITransactionMatches[];
	invoiceNumber?: string;
	chargebackWinLoss?: string;
	externalReferenceID?: string;
	disputeReasonCode?: string;
	is3D?: string;
	issuer?: string | null;
}

export interface ITransactionMatches {
	amount: number;
	arn: string;
	authorizationId: string;
	bin: string;
	createdBy: string;
	createdOn: string;
	currency: string;
	disputeQueueItemId: string;
	dynamicDescriptor: string;
	id: string;
	isDeleted: boolean;
	last4: string;
	merchant: string;
	meta: string;
	modifiedBy: string;
	modifiedOn: string;
	note: string;
	orderId: string;
	providerTransactionToken: string;
	responseCode: string;
	siteId: string;
	status: number;
	transactionId: string;
}

interface IProps {
	children: any;
	disputeQueueItemId: string;
	potentialMatches: IPotentialMatch[];
	additionalClass?: string;
}

export const DisputesQueueItemsLink: FC<IProps> = (props) => {
	const { children, disputeQueueItemId, potentialMatches, additionalClass } = props;

	const { search, state } = useLocation();
	const { disputeQueueId } = useParams();

	const potentialMatchesTransactionIds: string[] = potentialMatches?.map((match: IPotentialMatch): string => match?.transactionId);

	return (
		<Link
			to={`/disputes/queue-item/${disputeQueueItemId}`}
			className={clsx('text-black fs-8 fc-text-overflow', additionalClass)}
			state={{
				disputeQueuesSearch: state?.disputeQueuesSearch,
				disputeQueueItem: {
					search,
					disputeQueueId,
					potentialMatchesTransactionIds,
				},
			}}
		>
			{children}
		</Link>
	);
};

export const defaultDisputesQueueItemsColumns: MRT_ColumnDef<IDisputeItem>[] = [
	{
		accessorKey: DisputesQueueItemsTableAccessorKey.potentialMatches,
		header: 'Potential matches',
		enableSorting: false,
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Potential match</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => {

			return (
				<TableCell
					additionalClass="d-flex align-items-center main-font-family text-black fs-7"
					density={table?.options?.state?.density}
					isTextOverflow={true}
				>
					<PotentialMatchColumn matchError={row?.original?.matchError} potentialMatches={renderedCellValue}
										  allowReconciliation={row?.original?.allowReconciliation} />
				</TableCell>
			);
		}
	},
	{
		accessorKey: DisputesQueueItemsTableAccessorKey.createdOn,
		header: 'Created on',
		enableSorting: false,
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Created on</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => {
			const year = new Date(renderedCellValue).getFullYear();
			return (
				<DisputesQueueItemsLink disputeQueueItemId={row?.original?.id} potentialMatches={row?.original?.potentialMatches}>
					{
						year === 1
							? <div className="fw-bolder">-</div>
							: (
								<TableCell
									additionalClass="main-font-family text-black"
									density={table?.options?.state?.density}
									isTextOverflow={true}
								>
									{getTableDate(renderedCellValue)}
								</ TableCell>
							)
					}
				</DisputesQueueItemsLink>
			);
		}
	},
	{
		accessorKey: DisputesQueueItemsTableAccessorKey.requestDate,
		header: 'Request date',
		enableSorting: false,
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Request date</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => {
			const year = new Date(renderedCellValue).getFullYear();
			return (
				<DisputesQueueItemsLink disputeQueueItemId={row?.original?.id} potentialMatches={row?.original?.potentialMatches}>
					{
						year === 1
							? <div className="fw-bolder">-</div>
							: (
								<TableCell
									additionalClass="main-font-family text-black"
									density={table?.options?.state?.density}
									isTextOverflow={true}
								>
									{getTableDate(renderedCellValue)}
								</ TableCell>
							)
					}
				</DisputesQueueItemsLink>
			);
		}
	},
	{
		accessorKey: DisputesQueueItemsTableAccessorKey.authorizationId,
		header: 'Authorization code',
		enableSorting: false,
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Authorization code</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black fs-8"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<DisputesQueueItemsLink disputeQueueItemId={row?.original?.id} potentialMatches={row?.original?.potentialMatches}>
					<div className="fc-text-overflow">
						{renderedCellValue ? renderedCellValue : '-'}
					</div>
				</DisputesQueueItemsLink>
			</TableCell>
		),
	},
	{
		accessorKey: DisputesQueueItemsTableAccessorKey.disputeType,
		header: 'Dispute type',
		enableSorting: false,
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Dispute type</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black fs-8"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<DisputesQueueItemsLink disputeQueueItemId={row?.original?.id} potentialMatches={row?.original?.potentialMatches}>
					<div className="fc-text-overflow">
						{renderedCellValue}
					</div>
				</DisputesQueueItemsLink>
			</TableCell>
		),
	},
	{
		accessorKey: DisputesQueueItemsTableAccessorKey.stage,
		header: 'Stage',
		enableSorting: false,
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Stage</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black fs-8"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<DisputesQueueItemsLink disputeQueueItemId={row?.original?.id} potentialMatches={row?.original?.potentialMatches}>
					<div className="fc-text-overflow">
						{renderedCellValue}
					</div>
				</DisputesQueueItemsLink>
			</TableCell>
		),
	},
	{
		accessorKey: DisputesQueueItemsTableAccessorKey.providerName,
		header: 'Source',
		enableSorting: false,
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Source</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black fs-8"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<DisputesQueueItemsLink disputeQueueItemId={row?.original?.id} potentialMatches={row?.original?.potentialMatches}>
					<div className="fc-text-overflow">
						{renderedCellValue ? renderedCellValue : '-'}
					</div>
				</DisputesQueueItemsLink>
			</TableCell>
		),
	},
	{
		accessorKey: DisputesQueueItemsTableAccessorKey.amount,
		header: 'Amount',
		enableSorting: false,
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Amount</span>,
		Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex flex-column align-items-start main-font-family fc-text-overflow"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				<DisputesQueueItemsLink disputeQueueItemId={row?.original?.id} potentialMatches={row?.original?.potentialMatches}>
					{getAmount(renderedCellValue, row.original.currency)}
				</DisputesQueueItemsLink>
			</TableCell>
		),
	},
	{
		accessorKey: DisputesQueueItemsTableAccessorKey.cardBrand,
		header: 'Card brand',
		enableSorting: false,
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Card brand</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family fs-8"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<DisputesQueueItemsLink disputeQueueItemId={row?.original?.id} potentialMatches={row?.original?.potentialMatches}>
					<div className="fc-text-overflow">
						{renderedCellValue}
					</div>
				</DisputesQueueItemsLink>
			</TableCell>
		),
	},
	{
		accessorKey: DisputesQueueItemsTableAccessorKey.bin,
		header: 'Bin / Last 4',
		enableSorting: false,
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Bin / Last 4</span>,
		Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family fs-8"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<DisputesQueueItemsLink disputeQueueItemId={row?.original?.id} potentialMatches={row?.original?.potentialMatches}>
					{getBinLast4(renderedCellValue, row?.original?.last4)}
				</DisputesQueueItemsLink>
			</TableCell>
		),
	},
	{
		accessorKey: DisputesQueueItemsTableAccessorKey.arn,
		header: 'ARN',
		enableSorting: false,
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">ARN</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family fs-8 d-flex align-items-center"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
					<div className="d-flex align-items-center fc-text-overflow">
						{renderedCellValue && <CopyComponent additionalClass="fc-copy-icon" value={renderedCellValue} title="ARN" />}
						<DisputesQueueItemsLink disputeQueueItemId={row?.original?.id} potentialMatches={row?.original?.potentialMatches} >
							<div className="fc-text-overflow">
								{renderedCellValue ? renderedCellValue : '-'}
							</div>
						</DisputesQueueItemsLink>
					</div>
			</TableCell>
		),
	},
];

export const defaultDisputesArchivedColumns: MRT_ColumnDef<IDisputeItem>[] = [
	{
		id: DisputesQueueItemsTableAccessorKey.disputeQueueName,
		header: 'Queue name',
		enableSorting: false,
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Queue name</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black fs-8"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				{renderedCellValue ? renderedCellValue : '-'}
			</TableCell>
		)
	},
	{
		accessorKey: DisputesQueueItemsTableAccessorKey.createdOn,
		header: 'Created on',
		enableSorting: false,
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Created on</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => {
			const year = new Date(renderedCellValue).getFullYear();
			return (
				year === 1
					? <div className="fw-bolder">-</div>
					: (
						<TableCell
							additionalClass="main-font-family text-black"
							density={table?.options?.state?.density} isTextOverflow={false}
						>
							{getTableDate(renderedCellValue)}
						</ TableCell>
					)
			);
		}
	},
	{
		accessorKey: DisputesQueueItemsTableAccessorKey.requestDate,
		header: 'Request date',
		enableSorting: false,
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Request date</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => {
			const year = new Date(renderedCellValue).getFullYear();
			return (
				year === 1
					? <div className="fw-bolder">-</div>
					: (
						<TableCell
							additionalClass="main-font-family text-black"
							density={table?.options?.state?.density} isTextOverflow={false}
						>
							{getTableDate(renderedCellValue)}
						</ TableCell>
					)
			);
		}
	},
	{
		accessorKey: DisputesQueueItemsTableAccessorKey.disputeType,
		header: 'Dispute type',
		enableSorting: false,
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Dispute type</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black fs-8"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<div className="fc-text-overflow">
					{renderedCellValue}
				</div>
			</TableCell>
		),
	},
	{
		accessorKey: DisputesQueueItemsTableAccessorKey.stage,
		header: 'Stage',
		enableSorting: false,
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Stage</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black fs-8"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<div className="fc-text-overflow">
					{renderedCellValue}
				</div>
			</TableCell>
		),
	},
	{
		accessorKey: DisputesQueueItemsTableAccessorKey.providerName,
		header: 'Source',
		enableSorting: false,
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Source</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black fs-8"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<div className="fc-text-overflow">
					{renderedCellValue ? renderedCellValue : '-'}
				</div>
			</TableCell>
		),
	},
	{
		accessorKey: DisputesQueueItemsTableAccessorKey.amount,
		header: 'Amount',
		enableSorting: false,
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Amount</span>,
		Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex flex-column align-items-start main-font-family"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				{getAmount(renderedCellValue, row.original.currency)}
			</TableCell>
		),
	},
	{
		accessorKey: DisputesQueueItemsTableAccessorKey.cardBrand,
		header: 'Card brand',
		enableSorting: false,
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Card brand</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family fs-8"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<div className="fc-text-overflow">
					{renderedCellValue}
				</div>
			</TableCell>
		),
	},
	{
		accessorKey: DisputesQueueItemsTableAccessorKey.bin,
		header: 'Bin / Last 4',
		enableSorting: false,
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Bin / Last 4</span>,
		Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family fs-8"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				{getBinLast4(renderedCellValue, row?.original?.last4)}
			</TableCell>
		),
	},
	{
		accessorKey: DisputesQueueItemsTableAccessorKey.arn,
		header: 'ARN',
		enableSorting: false,
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">ARN</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family fs-8 d-flex align-items-center"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				{renderedCellValue && <CopyComponent additionalClass="fc-copy-icon" value={renderedCellValue} title="ARN" />}
				<div className="fc-text-overflow">
					{renderedCellValue ? renderedCellValue : '-'}
				</div>
			</TableCell>
		),
	},
];

export const defaultDisputesQueuesColumns: MRT_ColumnDef<IDisputeItem>[] = [
	{
		accessorKey: DisputesQueueTableAccessorKey.partnerId,
		header: 'Partner',
		enableSorting: false,
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Partner</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black fs-8"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<RelatedEntityNameLink id={renderedCellValue} type={RELATED_ENTITY_TYPE.PARTNER} />
			</TableCell>
		),
	},
	{
		accessorKey: DisputesQueueTableAccessorKey.createdOn,
		header: 'Created on',
		enableSorting: false,
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Created on</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black fs-8"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				<DateComponent
					value={renderedCellValue}
					additionalContainerClass="fc-text-overflow"
					additionalDateClass='fc-text-overflow'
				/>
			</TableCell>
		)
	},
	{
		accessorKey: DisputesQueueTableAccessorKey.name,
		header: 'Name',
		enableSorting: false,
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Name</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black fs-8"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<div className="fc-text-overflow">
					{renderedCellValue ? renderedCellValue : '-'}
				</div>
			</TableCell>
		),
	},
	{
		id: 'records',
		header: 'Records',
		columns: [
			{
				accessorKey: DisputesQueueTableAccessorKey.totalCount,
				header: 'Total',
				enableSorting: false,
				Header: <span className="text-muted fw-bolder main-font-family">Total</span>,
				Cell: ({ renderedCellValue, table }: any): JSX.Element => (
					<TableCell
						additionalClass="d-flex align-items-center main-font-family text-black fs-8 table-row-paddings"
						density={table?.options?.state?.density}
						isTextOverflow={true}
					>
						{renderedCellValue}
					</TableCell>
				)
			},
			{
				accessorKey: DisputesQueueTableAccessorKey.processedRecordsCount,
				header: 'Processed items',
				enableSorting: false,
				Header: <span className="text-muted fw-bolder main-font-family">Processed items</span>,
				Cell: ({ renderedCellValue, table }: any): JSX.Element => (
					<TableCell
						additionalClass="d-flex align-items-center main-font-family text-black fs-8"
						density={table?.options?.state?.density}
						isTextOverflow={true}
					>
						{renderedCellValue}
					</TableCell>
				)
			},
			{
				accessorKey: DisputesQueueTableAccessorKey.recordsCount,
				header: 'Unprocessed items',
				enableSorting: false,
				Header: <span className="text-muted fw-bolder main-font-family">Unprocessed items</span>,
				Cell: ({ renderedCellValue, table }: any): JSX.Element => (
					<TableCell
						additionalClass="d-flex align-items-center main-font-family text-black fs-8"
						density={table?.options?.state?.density}
						isTextOverflow={true}
					>
						{renderedCellValue}
					</TableCell>
				)
			},
			{
				accessorKey: DisputesQueueTableAccessorKey.errorCount,
				header: 'Records with errors',
				enableSorting: false,
				Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Records with errors</span>,
				Cell: ({ renderedCellValue, table }: any): JSX.Element => (
					<TableCell
						additionalClass="main-font-family fs-8"
						density={table?.options?.state?.density}
						isTextOverflow={false}
					>
						<div className="fc-text-overflow">
							{renderedCellValue}
						</div>
					</TableCell>
				),
			},
			{
				accessorKey: DisputesQueueTableAccessorKey.loadedRecordsCount,
				header: 'File progress',
				enableSorting: false,
				Header: <span className="text-muted fw-bolder main-font-family">File Progress</span>,
				Cell: ({ renderedCellValue, row, table }: any): JSX.Element => {
					const progress = Math.round((row.original.loadedRecordsCount + row.original.errorCount) * 100 / row.original.totalCount );
					const isValidValue = !isNaN(progress) && isFinite(progress);

					return (
						<TableCell
							additionalClass="d-flex align-items-center main-font-family text-black"
							density={table?.options?.state?.density}
							isTextOverflow={true}
						>
							{renderedCellValue}/
							{
								isValidValue && progress === 100 && (
									<span className="dispute-queue-progress-icon">
										<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
											<path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22ZM11.7 17.7L16.7 12.7C17.1 12.3 17.1 11.7 16.7 11.3C16.3 10.9 15.7 10.9 15.3 11.3L11 15.6L8.70001 13.3C8.30001 12.9 7.69999 12.9 7.29999 13.3C6.89999 13.7 6.89999 14.3 7.29999 14.7L10.3 17.7C10.5 17.9 10.8 18 11 18C11.2 18 11.5 17.9 11.7 17.7Z" fill="black"/>
											<path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black"/>
										</svg>
									</span>
								)
							}
							{
								progress !== 100 && (
									<span className={isValidValue && progress === 100 ? 'dispute-queue-progress-full' : 'dispute-queue-progress-not-full'}>
										{isValidValue ? progress : 0}%
									</span>
								)
							}
						</TableCell>
					);
				}
			},
		],
	},
	{
		accessorKey: DisputesQueueTableAccessorKey.errors,
		header: 'Errors',
		enableSorting: false,
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Errors</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family fs-8"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<CustomWidthTooltip
					// TransitionComponent={(): any => <span>{renderedCellValue}</span>}
					// title={renderedCellValue?.replace(/Error:/gi, '\n')}>
					title={renderedCellValue}>
					<div className="error-tooltip-button">{renderedCellValue}</div>
				</CustomWidthTooltip>
			</TableCell>
		),
	},
];

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
	<Tooltip {...props} classes={{ popper: className }} />
))({
	[`& .${tooltipClasses.tooltip}`]: {
		maxWidth: 500,
	},
});
